import { getTheMostOccurringNumber, isTheSameColumn, isTheSameRow } from '../smart-select.helper';

export default class SmartSelectGroup {
  #elements;
  #topToBottomElements;
  #topMostElement;
  #bottomMostElement;
  #leftToRightElements;
  #leftMostElement;
  #rightMostElement;
  #betweenTopAndBottomElements;
  #betweenLeftAndRightElements;
  #distanceBetweenLeftMostAndRightMostElement;
  #distanceBetweenTopMostAndBottomMostElement;
  #mostCommonVerticalGap;
  #mostCommonHorizontalGap;

  constructor (smartElements) {
    this.#elements = smartElements;
    this.calculateElements();
  }

  calculateElements () {
    this.#topToBottomElements = this.#elements.slice(0).sort((a, b) => a.getCoordinator().top - b.getCoordinator().top);
    this.#topMostElement = this.#topToBottomElements[0];
    this.#bottomMostElement = this.#elements.slice(0).sort((a, b) => b.getCoordinator().getBottom() - a.getCoordinator().getBottom())[0];

    this.#leftToRightElements = this.#elements.slice(0).sort((a, b) => a.getCoordinator().left - b.getCoordinator().left);
    this.#leftMostElement = this.#leftToRightElements[0];
    this.#rightMostElement = this.#elements.slice(0).sort((a, b) => b.getCoordinator().getRight() - a.getCoordinator().getRight())[0];

    this.#betweenLeftAndRightElements = this.#leftToRightElements.filter(elem => elem.getId() !== this.#leftMostElement.getId() && elem.getId() !== this.#rightMostElement.getId());
    this.#betweenTopAndBottomElements = this.#topToBottomElements.filter(elem => elem.getId() !== this.#topMostElement.getId() && elem.getId() !== this.#bottomMostElement.getId());

    this.#distanceBetweenLeftMostAndRightMostElement = this.#rightMostElement.getCoordinator().left - this.#leftMostElement.getCoordinator().getRight();
    this.#distanceBetweenTopMostAndBottomMostElement = this.#bottomMostElement.getCoordinator().top - this.#topMostElement.getCoordinator().getBottom();

    this.#mostCommonVerticalGap = this.#calculateMostCommonVerticalGap();
    this.#mostCommonHorizontalGap = this.#calculateMostCommonHorizontalGap();
  }

  getTopToBottomElements () {
    return this.#topToBottomElements;
  }

  getTopMostElement () {
    return this.#topMostElement;
  }

  getBottomMostElement () {
    return this.#bottomMostElement;
  }

  getLeftToRightElements () {
    return this.#leftToRightElements;
  }

  getLeftMostElement () {
    return this.#leftMostElement;
  }

  getRightMostElement () {
    return this.#rightMostElement;
  }

  getElements () {
    return this.#elements;
  }

  getBetweenTopAndBottomElements () {
    return this.#betweenTopAndBottomElements;
  }

  getBetweenLeftAndRightElements () {
    return this.#betweenLeftAndRightElements;
  }

  getDistanceBetweenLeftMostAndRightMostElement () {
    return this.#distanceBetweenLeftMostAndRightMostElement;
  }

  getDistanceBetweenTopMostAndBottomMostElement () {
    return this.#distanceBetweenTopMostAndBottomMostElement;
  }

  #calculateMostCommonVerticalGap () {
    const gapsBetweenElements = this.#topToBottomElements.map((elem, index) => {
      if (index === 0) {
        return undefined;
      } else {
        return Math.floor(elem.getCoordinator().top - this.#topToBottomElements[index - 1].getCoordinator().getBottom());
      }
    }).filter((gap) => gap !== undefined).sort((a, b) => Math.abs(a) - Math.abs(b));

    return getTheMostOccurringNumber(gapsBetweenElements);
  }

  #calculateMostCommonHorizontalGap () {
    const gapsBetweenElements = this.#leftToRightElements.map((elem, index) => {
      if (index === 0) {
        return undefined;
      } else {
        return Math.floor(elem.getCoordinator().left - this.#leftToRightElements[index - 1].getCoordinator().getRight());
      }
    }).filter((gap) => gap !== undefined).sort((a, b) => Math.abs(a) - Math.abs(b));

    return getTheMostOccurringNumber(gapsBetweenElements);
  }

  getMostCommonVerticalGap () {
    return this.#mostCommonVerticalGap;
  }

  getMostCommonHorizontalGap () {
    return this.#mostCommonHorizontalGap;
  }

  getTopBorder () {
    return this.#topMostElement.getCoordinator().top;
  }

  getLeftBorder () {
    return this.#leftMostElement.getCoordinator().left;
  }

  getBottomBorder () {
    return this.#bottomMostElement.getCoordinator().getBottom();
  }

  getRightBorder () {
    return this.#rightMostElement.getCoordinator().getRight();
  }

  getWidth () {
    return this.getRightBorder() - this.getLeftBorder();
  }

  getMaxLeftDistance () {
    return this.#rightMostElement.getCoordinator().left - this.#leftMostElement.getCoordinator().left;
  }

  getMaxTopDistance () {
    return this.#bottomMostElement.getCoordinator().top - this.#topMostElement.getCoordinator().top;
  }

  getHeight () {
    return this.getBottomBorder() - this.getTopBorder();
  }

  getNumberOfRows () {
    let usingElements = [...this.#leftToRightElements];
    const visitedElements = [];

    while (usingElements.length > 0) {
      const element = usingElements.shift();
      visitedElements.push(element);

      usingElements = usingElements.filter(
        (elem) =>
          !isTheSameRow(elem, element)
      );
    }

    return visitedElements.length;
  }

  getNumberOfColumns () {
    let usingElements = [...this.#topToBottomElements];
    const visitedElements = [];

    while (usingElements.length > 0) {
      const element = usingElements.shift();
      visitedElements.push(element);

      usingElements = usingElements.filter(
        (elem) =>
          !isTheSameColumn(elem, element)
      );
    }

    return visitedElements.length;
  }
}
